// Graphics quality: Low / High detail, and the automatic choice on the first load (pure logic, no three.js or DOM, so
// quality.test.ts can check it). graphicsQuality.ts and Game.ApplyQuality apply a level to the running game;
// settings.ts stores the choice.
//
//   auto  (default): the first time the game runs, FrameRateProbe measures the frame rate for a few seconds at High;
//         under AUTO_LOW_FPS it picks Low. The result is stored (settings.detectedQuality), so it is measured once per
//         browser; choosing Auto again in Settings measures again.
//   low:  pixel ratio capped at 1, half-resolution textures, fewer segments (balls, pockets, legs), no antialiasing,
//         a simpler room (no chairs, cue rack, picture or panel mouldings). The soft ball shadows stay.
//   high: everything, and the furniture with rounded edges and soft cushions.
// Switching applies at once (the table, room and balls are rebuilt), except antialiasing, which WebGL fixes when the
// canvas is created: it follows on the next page load.

export type QualityChoice = "auto" | "low" | "high";
export type QualityLevel = "low" | "high";

export const QUALITY_CHOICES: QualityChoice[] = ["auto", "low", "high"];

/** Auto picks Low below this frame rate (frames per second, measured at High). */
export const AUTO_LOW_FPS = 40;

export interface QualityProfile {
    level: QualityLevel;
    /** cap on window.devicePixelRatio for the canvas */
    maxPixelRatio: number;
    antialias: boolean;
    /** texture resolution, 1 = as loaded or generated */
    textureScale: number;
    /** ball sphere segments (width, height) */
    ballSegments: [number, number];
    /** extra points per edge on the curved parts of the cushion line (jaws, pocket backs); 1 = the physics points only */
    curveSubdivisions: number;
    /** points per rounded corner of the rails */
    railCornerSegments: number;
    legSegments: number;
    /** chairs, cue rack, the second picture, panel mouldings and profiled skirting */
    roomProps: boolean;
    /** subdivisions of the room's large surfaces (the baked lamp light is per vertex), relative to High */
    roomSubdivision: number;
    /** steps per quarter round on the furniture's edges (chairs, bench) and soft, puffed cushions; 0 = square boxes */
    furnitureBevel: number;
}

export const QUALITY_PROFILES: Record<QualityLevel, QualityProfile> = {
    high: {
        level: "high", maxPixelRatio: 2, antialias: true, textureScale: 1, ballSegments: [32, 20],
        curveSubdivisions: 3, railCornerSegments: 14, legSegments: 18,
        roomProps: true, roomSubdivision: 1, furnitureBevel: 4,
    },
    low: {
        level: "low", maxPixelRatio: 1, antialias: false, textureScale: 0.5, ballSegments: [18, 12],
        curveSubdivisions: 1, railCornerSegments: 6, legSegments: 10,
        roomProps: false, roomSubdivision: 0.5, furnitureBevel: 0,
    },
};

export function isQualityChoice(v: unknown): v is QualityChoice {
    return typeof v === "string" && (QUALITY_CHOICES as string[]).includes(v);
}

export function isQualityLevel(v: unknown): v is QualityLevel {
    return v === "low" || v === "high";
}

/** The level to draw with: the player's choice, or for auto the measured one (High until measured). */
export function effectiveQuality(s: {quality: QualityChoice, detectedQuality: QualityLevel | null}): QualityLevel {
    if (s.quality === "low" || s.quality === "high") return s.quality;
    return s.detectedQuality || "high";
}

/** Does auto still need to measure? */
export function needsProbe(s: {quality: QualityChoice, detectedQuality: QualityLevel | null}): boolean {
    return s.quality === "auto" && !s.detectedQuality;
}

/** The automatic choice for a measured frame rate. */
export function chooseQuality(fps: number): QualityLevel {
    return fps < AUTO_LOW_FPS ? "low" : "high";
}

export function devicePixelRatioFor(profile: QualityProfile, windowRatio: number | undefined): number {
    const r = windowRatio && isFinite(windowRatio) && windowRatio > 0 ? windowRatio : 1;
    return Math.min(r, profile.maxPixelRatio);
}

/**
 * Measures the frame rate from the rendered frames' timestamps (performance.now()): it skips a warm-up (texture
 * uploads, the cue model loading), then counts frames over a window. A gap longer than maxGapMs (the tab hidden, the
 * machine asleep, a debugger) restarts the measurement instead of counting as a slow frame. frame() returns the
 * choice once, when the window is complete, and null before and after.
 */
export class FrameRateProbe {
    private start = -1;
    private measureStart = -1;
    private last = -1;
    private frames = 0;
    private done = false;
    fps = 0;

    constructor(readonly warmupMs = 1500, readonly windowMs = 3000, readonly maxGapMs = 1000) {}

    get finished() { return this.done; }

    frame(t: number): QualityLevel | null {
        if (this.done) return null;
        if (this.last >= 0 && t - this.last > this.maxGapMs) this.start = -1;   // a stall or a hidden tab: start again
        this.last = t;
        if (this.start < 0) { this.start = t; this.measureStart = -1; return null; }
        if (t - this.start < this.warmupMs) return null;
        if (this.measureStart < 0) { this.measureStart = t; this.frames = 0; return null; }
        this.frames++;
        const measured = t - this.measureStart;
        if (measured < this.windowMs) return null;
        this.fps = this.frames / (measured / 1000);
        this.done = true;
        return chooseQuality(this.fps);
    }
}
